import { CheckIcon } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import type { MouseEvent as ReactMouseEvent } from 'react';

import clsxm from '@/lib/clsxm';

import type { MediaTileSelection } from './MediaSelectionProvider';

// Children of a tile that shrink when it is selected; the selection layer
// itself is left alone so its ring and check stay at the tile's edge.
const SHRINK_CHILDREN =
  '[&>*:not([data-selection-layer])]:transition-transform [&>*:not([data-selection-layer])]:duration-200 [&>*:not([data-selection-layer])]:ease-out';
const SHRUNK_CHILDREN =
  '[&>*:not([data-selection-layer])]:scale-[0.9] [&>*:not([data-selection-layer])]:rounded-md';

/** Classes for the tile container (it must be `relative overflow-hidden`). */
export const mediaTileSelectionClassName = (selection: MediaTileSelection) =>
  selection.enabled
    ? clsxm(
        // Keep the tile's layers (selection z-70, heart z-50, 360 badge z-60)
        // inside the tile so they can't paint over the floating action bar
        // or page overlays.
        'isolate transition-colors duration-200',
        SHRINK_CHILDREN,
        // The tile's own focus ring would linger on whichever tile was
        // clicked last; selection draws its own ring.
        selection.active && 'focus-within:ring-0 focus-within:ring-offset-0',
        selection.selected &&
          clsxm('bg-primary-50 dark:bg-primary-500/15', SHRUNK_CHILDREN),
      )
    : '';

interface MediaTileSelectionLayerProps {
  selection: MediaTileSelection;
  /** Read out to screen readers, e.g. the prompt. */
  label?: string;
}

/**
 * The check circle (on hover, or always while selecting) and, in selection
 * mode, a click target over the whole tile so a click selects instead of
 * opening the item. Shift-click selects a range.
 */
const MediaTileSelectionLayer = ({
  selection,
  label,
}: MediaTileSelectionLayerProps) => {
  const { t } = useLocale('media-library');
  if (!selection.enabled) return null;

  const { selected, active } = selection;
  const handleClick = (event: ReactMouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    selection.toggle(event.shiftKey);
  };
  // Shift-click would otherwise also select page text.
  const preventShiftSelection = (event: ReactMouseEvent) => {
    if (event.shiftKey) event.preventDefault();
  };
  const actionLabel = selected ? t('Deselect') : t('Select');
  const ariaLabel = label ? `${actionLabel}: ${label}` : actionLabel;

  return (
    <div
      data-selection-layer=''
      className='pointer-events-none absolute inset-0 z-[70] select-none'
    >
      <div
        aria-hidden='true'
        className={clsxm(
          'absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-black/40 to-transparent transition-opacity duration-200',
          active && !selected
            ? 'opacity-60'
            : selected
              ? 'opacity-0'
              : 'opacity-0 group-hover:opacity-100',
        )}
      />
      {selected && (
        <div
          aria-hidden='true'
          className='absolute inset-0 rounded-lg ring-2 ring-inset ring-primary-500'
        />
      )}
      {active && (
        <button
          type='button'
          aria-pressed={selected}
          aria-label={ariaLabel}
          onMouseDown={preventShiftSelection}
          onClick={handleClick}
          className='pointer-events-auto absolute inset-0 h-full w-full cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400'
        />
      )}
      <button
        type='button'
        tabIndex={active ? -1 : 0}
        aria-pressed={selected}
        aria-label={ariaLabel}
        title={actionLabel}
        onMouseDown={preventShiftSelection}
        onClick={handleClick}
        className={clsxm(
          'group/check pointer-events-auto absolute start-2 top-2 flex h-6 w-6 items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-white',
          selected
            ? 'bg-primary-500 text-white shadow-md shadow-primary-900/30 ring-2 ring-white'
            : 'bg-black/25 text-white ring-[1.5px] ring-white/90 backdrop-blur-[2px] hover:bg-black/45',
          active || selected
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100 [@media(hover:none)]:hidden',
        )}
      >
        <CheckIcon
          aria-hidden='true'
          className={clsxm(
            'h-3.5 w-3.5 transition-all duration-200',
            selected
              ? 'scale-100 opacity-100'
              : 'scale-75 opacity-0 group-hover/check:scale-90 group-hover/check:opacity-80',
          )}
        />
      </button>
    </div>
  );
};

export default MediaTileSelectionLayer;
